import { Entity, Query, Dimension } from '../api/OasisBackendApi';
import { PCAResult, transformPointsThroughPCA } from './pca';

export interface DimensionDifference {
    dimension: Dimension;
    value1: number;
    value2: number;
    difference: number;
    contributionPercent?: number;
    xContribution?: number; // Contribution to X component
    yContribution?: number; // Contribution to Y component
}

export interface ComparisonResult {
    coordinateDiff: { x: number; y: number; distance: number };
    dimensionDifferences: DimensionDifference[];
    dimensionDifferencesUnsorted?: DimensionDifference[]; // For PCA path plotting (order matters)
    point1: { entity?: Entity; query?: Query; isSearchResult?: boolean };
    point2: { entity?: Entity; query?: Query; isSearchResult?: boolean };
}

/**
 * Get dimension value from entity or query.
 * When useNormalized is true, uses the normalized embeddings used for actual search computation.
 */
function getDimensionValue(
    point: { entity?: Entity; query?: Query },
    dimension: Dimension,
    useNormalized: boolean = false
): number | undefined {
    if (point.entity) {
        const metadata = point.entity.embedding?.metadata;
        const preferredKey = useNormalized ? 'normalized_entity_dimension_index' : 'entity_dimension_index';
        const dimensionIndex = metadata?.[preferredKey] ?? metadata?.['entity_dimension_index'];
        if (dimensionIndex) {
            return dimensionIndex[dimension.name] !== undefined
                ? dimensionIndex[dimension.name]
                : dimensionIndex[dimension.id];
        }
    } else if (point.query) {
        const preferred = useNormalized ? point.query.normalized_oasis_embedding : point.query.oasis_embedding;
        const oasisEmbedding = preferred ?? point.query.oasis_embedding;
        if (oasisEmbedding) {
            const val = oasisEmbedding[dimension.name] !== undefined
                ? oasisEmbedding[dimension.name]
                : oasisEmbedding[dimension.id];
            return val ?? undefined;
        }
    }
    return undefined;
}

/**
 * Get point name for display
 */
function getPointName(point: { entity?: Entity; query?: Query }): string {
    if (point.entity) {
        return point.entity.content?.title || point.entity.content?.name || `Entity ${point.entity.id}`;
    } else if (point.query) {
        return point.query.text || `Query ${point.query.id}`;
    }
    return 'Unknown';
}

/**
 * Calculate comparison in normal mode (direct dimension differences)
 */
export function calculateNormalComparison(
    point1: { x: number; y: number; entity?: Entity; query?: Query; isSearchResult?: boolean },
    point2: { x: number; y: number; entity?: Entity; query?: Query; isSearchResult?: boolean },
    dimensions: Dimension[],
    selectedDimensions?: { x: Dimension | null; y: Dimension | null },
    useNormalized: boolean = false
): ComparisonResult {
    const coordinateDiff = {
        x: point2.x - point1.x,
        y: point2.y - point1.y,
        distance: Math.sqrt(Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2))
    };

    const dimensionDifferences: DimensionDifference[] = [];
    
    // In normal mode, only include the selected X and Y dimensions
    const dimensionsToCompare: Dimension[] = [];
    if (selectedDimensions?.x) {
        dimensionsToCompare.push(selectedDimensions.x);
    }
    if (selectedDimensions?.y && selectedDimensions.y.id !== selectedDimensions.x?.id) {
        dimensionsToCompare.push(selectedDimensions.y);
    }
    
    // If no dimensions are selected, don't show any contributions
    if (dimensionsToCompare.length === 0) {
        return {
            coordinateDiff,
            dimensionDifferences: [],
            point1: { entity: point1.entity, query: point1.query, isSearchResult: point1.isSearchResult },
            point2: { entity: point2.entity, query: point2.query, isSearchResult: point2.isSearchResult }
        };
    }
    
    for (const dimension of dimensionsToCompare) {
        const value1 = getDimensionValue(point1, dimension, useNormalized);
        const value2 = getDimensionValue(point2, dimension, useNormalized);
        
        if (value1 !== undefined && value2 !== undefined) {
            const difference = value2 - value1;
            // Only include dimensions that differ (above threshold to avoid floating point noise)
            // Use a more reasonable threshold: 0.001 or 0.1% of the larger absolute value
            const threshold = Math.max(0.001, Math.max(Math.abs(value1), Math.abs(value2)) * 0.001);
            if (Math.abs(difference) > threshold) {
                // Calculate X and Y contributions
                // In normal mode, only the selected dimensions contribute to X/Y
                let xContribution = 0;
                let yContribution = 0;
                
                if (selectedDimensions?.x?.id === dimension.id) {
                    xContribution = difference;
                }
                if (selectedDimensions?.y?.id === dimension.id) {
                    yContribution = difference;
                }
                
                dimensionDifferences.push({
                    dimension,
                    value1,
                    value2,
                    difference,
                    xContribution,
                    yContribution
                });
            }
        }
    }
    
    // Sort by absolute difference (largest first)
    dimensionDifferences.sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference));
    
    // Calculate contribution percentages
    const totalAbsDifference = dimensionDifferences.reduce((sum, d) => sum + Math.abs(d.difference), 0);
    if (totalAbsDifference > 0) {
        dimensionDifferences.forEach(d => {
            d.contributionPercent = (Math.abs(d.difference) / totalAbsDifference) * 100;
        });
    }

    return {
        coordinateDiff,
        dimensionDifferences,
        point1: { entity: point1.entity, query: point1.query, isSearchResult: point1.isSearchResult },
        point2: { entity: point2.entity, query: point2.query, isSearchResult: point2.isSearchResult }
    };
}

/**
 * Calculate comparison in PCA mode (transform difference back to dimension space)
 */
export function calculatePCAComparison(
    point1: { x: number; y: number; entity?: Entity; query?: Query; isSearchResult?: boolean },
    point2: { x: number; y: number; entity?: Entity; query?: Query; isSearchResult?: boolean },
    pcaResult: PCAResult,
    pcaSelectedDimensions: Dimension[],
    useNormalized: boolean = false
): ComparisonResult {
    const coordinateDiff = {
        x: point2.x - point1.x,
        y: point2.y - point1.y,
        distance: Math.sqrt(Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2))
    };

    // Calculate contributions using loadings × standardized differences
    // This is mathematically correct: when dimension i changes by diff_i,
    // the contribution in PCA space is loading_i × (diff_i / std_i)
    
    // Get dimension values using the order from pcaResult.dimensionVectors
    const point1Values: number[] = [];
    const point2Values: number[] = [];
    
    for (const dimVector of pcaResult.dimensionVectors) {
        const value1 = getDimensionValue(point1, dimVector.dimension, useNormalized) ?? 0;
        const value2 = getDimensionValue(point2, dimVector.dimension, useNormalized) ?? 0;
        point1Values.push(value1);
        point2Values.push(value2);
    }
    
    // Build intermediate points by progressively adding each dimension's difference
    // This guarantees the path ends at point2
    const intermediatePoints: number[][] = [];
    const currentPoint = [...point1Values];
    intermediatePoints.push([...currentPoint]);
    
    for (let i = 0; i < point1Values.length; i++) {
        currentPoint[i] = point2Values[i]; // Set to point2's value
        intermediatePoints.push([...currentPoint]);
    }
    
    // Transform all intermediate points through PCA
    const transformedIntermediates = transformPointsThroughPCA(intermediatePoints, pcaResult);
    
    // Get loadings matrix
    const loadings = pcaResult.pcaModel.getLoadings();
    let loadingsArray: number[][];
    if (Array.isArray(loadings) && Array.isArray(loadings[0])) {
        loadingsArray = loadings as number[][];
    } else if (loadings && typeof (loadings as any).to2DArray === 'function') {
        loadingsArray = (loadings as any).to2DArray();
    } else if (loadings && typeof (loadings as any).getRow === 'function') {
        const matrix = loadings as any;
        loadingsArray = [];
        for (let i = 0; i < matrix.rows; i++) {
            loadingsArray.push(matrix.getRow(i));
        }
    } else {
        loadingsArray = pcaSelectedDimensions.map(() => [0, 0]);
    }
    
    
    // Calculate contributions as differences between consecutive transformed intermediate points
    const dimensionDifferences: DimensionDifference[] = [];
    
    for (let i = 0; i < pcaResult.dimensionVectors.length; i++) {
        const dimVector = pcaResult.dimensionVectors[i];
        const dimension = dimVector.dimension;
        
        // Get transformed points before and after this dimension changed
        const prevPoint = transformedIntermediates[i];
        const nextPoint = transformedIntermediates[i + 1];
        
        const xContribution = nextPoint[0] - prevPoint[0];
        const yContribution = nextPoint[1] - prevPoint[1];
        
        // Total contribution magnitude
        const contribution = Math.sqrt(xContribution * xContribution + yContribution * yContribution);
        
        // Include all dimensions to ensure vectors sum correctly
        dimensionDifferences.push({
            dimension,
            value1: point1Values[i],
            value2: point2Values[i],
            difference: contribution,
            xContribution,
            yContribution
        });
    }
    
    // Keep unsorted copy for PCA path plotting (order must match intermediate points)
    const dimensionDifferencesUnsorted = [...dimensionDifferences];
    
    // Sort by absolute contribution (largest first) for display
    dimensionDifferences.sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference));
    
    // Calculate contribution percentages
    const totalAbsContribution = dimensionDifferences.reduce((sum, d) => sum + Math.abs(d.difference), 0);
    if (totalAbsContribution > 0) {
        dimensionDifferences.forEach(d => {
            d.contributionPercent = (Math.abs(d.difference) / totalAbsContribution) * 100;
        });
        // Also set percentages on unsorted version
        dimensionDifferencesUnsorted.forEach(d => {
            d.contributionPercent = (Math.abs(d.difference) / totalAbsContribution) * 100;
        });
    }

    return {
        coordinateDiff,
        dimensionDifferences,
        dimensionDifferencesUnsorted,
        point1: { entity: point1.entity, query: point1.query, isSearchResult: point1.isSearchResult },
        point2: { entity: point2.entity, query: point2.query, isSearchResult: point2.isSearchResult }
    };
}

/**
 * Generate textual descriptions of differences
 */
export function generateDifferenceDescriptions(comparison: ComparisonResult): string[] {
    const descriptions: string[] = [];
    const point1Name = getPointName(comparison.point1);
    const point2Name = getPointName(comparison.point2);

    for (const diff of comparison.dimensionDifferences) {
        const absDiff = Math.abs(diff.difference);
        const direction = diff.difference > 0 ? 'higher' : 'lower';
        const otherPoint = diff.difference > 0 ? point2Name : point1Name;
        const thisPoint = diff.difference > 0 ? point1Name : point2Name;
        
        descriptions.push(
            `${diff.dimension.name}: ${thisPoint} scores ${absDiff.toFixed(3)} ${direction} than ${otherPoint}`
        );
    }

    return descriptions;
}
